import { AIEmptyFiles } from '@/constant/imgImport';
import useToast from '@/hooks/useToast';
import { ICollection } from '@/interfaces/doccen-lib/collection.interface';
import { patchOrderDoccenCollectionDocument } from 'apis/tms/library/collection.api';
import { deepClone } from 'common/other';
import { PATH_NAME } from 'configs/pathName';
import { Typography } from 'design-system/components/Typography';
import { useRouter } from 'next/router';
import { ComponentPropsWithoutRef, useEffect, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from 'react-beautiful-dnd';
import { useMutation } from 'react-query';
import DocumentItem from './DocumentItem';

type DocumentDragListProps = ComponentPropsWithoutRef<'div'> & {
  items: Array<ICollection['items'][number]>;
  isOwner: boolean;
};

function DocumentDragList(props: DocumentDragListProps) {
  const { items, isOwner } = props;
  const toast = useToast();
  const router = useRouter();

  const [internalItems, setInternalItems] =
    useState<Array<ICollection['items'][number]>>(items);

  const { cid: collectionId } = router.query;

  const { mutate: handleReorderedItem } = useMutation({
    mutationFn: patchOrderDoccenCollectionDocument,
    onSuccess: (data) => {
      if (collectionId !== data.collectionId) {
        router.replace({
          pathname: PATH_NAME.LIBRARY_COLLECTION_DETAIL,
          query: { cid: data.collectionId },
        });
      }
    },
    onError: () => {
      toast('Đã xảy ra lỗi sắp xếp document', 'error');
    },
  });

  const handleOnDragEnd = async (result: DropResult) => {
    if (!result.destination || !result.source) return;

    const destinationIndex = result.destination.index;
    const sourceIndex = result.source.index;
    const sourceItem = internalItems[sourceIndex - 1];

    if (destinationIndex === sourceIndex) return;

    setInternalItems((prev) => {
      const clone = deepClone(prev);

      clone.splice(
        destinationIndex < sourceIndex
          ? destinationIndex - 1
          : destinationIndex,
        0,
        sourceItem
      );

      clone.splice(
        destinationIndex < sourceIndex ? sourceIndex : sourceIndex - 1,
        1
      );

      return clone;
    });

    handleReorderedItem({
      collectionId: collectionId as string,
      itemId: sourceItem.id,
      newOrder: destinationIndex,
    });
  };

  useEffect(() => {
    setInternalItems(items);
  }, [items]);

  if (items.length === 0) {
    return renderEmptyFiles(isOwner);
  }

  const renderList = () => {
    if (!isOwner) {
      return (
        <div className="space-y-2">
          {items?.map((item) => {
            return <DocumentItem key={item.id} {...item} viewMode />;
          })}
        </div>
      );
    }

    return (
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Droppable droppableId="collectionItems">
          {(provided) => (
            <div
              className="space-y-2"
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {internalItems?.map((item, index) => {
                return (
                  <Draggable
                    key={item.id}
                    draggableId={item.id}
                    index={index + 1}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <DocumentItem key={item.id} {...item} />
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  };

  return <>{renderList()}</>;
}

export default DocumentDragList;

export const renderEmptyFiles = (isOwner: boolean) => {
  return (
    <div className="my-10 flex h-full flex-1 flex-col items-center justify-center space-y-6 px-10 text-Text/LightBackground/Secondary lg:my-0">
      <img src={AIEmptyFiles} alt="AIEmptyFiles" className="mx-auto" />
      <Typography
        variant="body2"
        className="text-center text-Text/LightBackground/Secondary"
      >
        {isOwner
          ? 'Hiện chưa có nội dung. Thêm mới nội dung ngay.'
          : 'Hiện chưa có nội dung.'}
      </Typography>
    </div>
  );
};
